// Pure, framework-free helpers for building and parsing CSS gradient strings.
// Kept separate from the presentational GradientPicker so the build <-> parse
// round-trip can be unit-tested in isolation. Gradient *type* is encoded in the
// CSS string itself (linear-gradient / radial-gradient / conic-gradient); there
// is no structural gradient field in the styling model.

export type GradientType = "linear" | "radial" | "conic";

export type RadialShape = "circle" | "ellipse";

export type RadialSize = "closest-side" | "closest-corner" | "farthest-side" | "farthest-corner";

export interface IGradientStop {
  color: string;
  position: number;
}

export interface IGradientPosition {
  x: number;
  y: number;
}

export interface IGradientState {
  type: GradientType;
  /** Linear: gradient angle. Conic: `from` angle. Unused for radial. */
  angle: number;
  stops: IGradientStop[];
  /** Radial + conic center, in percent. Defaults to {50,50}. */
  position?: IGradientPosition;
  /** Radial only. Defaults to "circle". */
  shape?: RadialShape;
  /** Radial only. Defaults to "farthest-corner". */
  size?: RadialSize;
}

export const DEFAULT_GRADIENT_POSITION: IGradientPosition = { x: 50, y: 50 };
export const DEFAULT_RADIAL_SHAPE: RadialShape = "circle";
export const DEFAULT_RADIAL_SIZE: RadialSize = "farthest-corner";
export const DEFAULT_LINEAR_ANGLE: number = 90;
export const DEFAULT_CONIC_ANGLE: number = 0;

export const RADIAL_SIZES: readonly RadialSize[] = [
  "closest-side",
  "closest-corner",
  "farthest-side",
  "farthest-corner"
];

const formatStops = (stops: IGradientStop[]): string =>
  stops.map((stop: IGradientStop): string => `${stop.color} ${stop.position}%`).join(", ");

/** Serialise a gradient working-state into a CSS gradient string. */
export const buildGradientString = (gradient: IGradientState): string => {
  const stopsStr: string = formatStops(gradient.stops);
  const pos: IGradientPosition = gradient.position ?? DEFAULT_GRADIENT_POSITION;

  if (gradient.type === "radial") {
    const shape: RadialShape = gradient.shape ?? DEFAULT_RADIAL_SHAPE;
    const size: RadialSize = gradient.size ?? DEFAULT_RADIAL_SIZE;
    return `radial-gradient(${shape} ${size} at ${pos.x}% ${pos.y}%, ${stopsStr})`;
  }

  if (gradient.type === "conic") {
    return `conic-gradient(from ${gradient.angle}deg at ${pos.x}% ${pos.y}%, ${stopsStr})`;
  }

  return `linear-gradient(${gradient.angle}deg, ${stopsStr})`;
};

// A color stop is a color token (#hex or rgb/rgba) immediately followed by a
// percentage. Requiring the leading color token prevents the radial/conic
// "at X% Y%" center coordinates from being mistaken for stops.
const COLOR_TOKEN: string = String.raw`#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)`;

const parseColorStops = (input: string): IGradientStop[] => {
  const stopRegex: RegExp = new RegExp(`(${COLOR_TOKEN})\\s*(\\d+(?:\\.\\d+)?)%`, "g");
  const stops: IGradientStop[] = [];
  const matches: IterableIterator<RegExpMatchArray> = input.matchAll(stopRegex);
  for (const match of matches) {
    stops.push({ color: match[1], position: Math.round(Number(match[2])) });
  }
  return stops;
};

const parsePosition = (css: string): IGradientPosition => {
  const posMatch: RegExpMatchArray | null = css.match(/at\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
  if (!posMatch) {
    return { ...DEFAULT_GRADIENT_POSITION };
  }
  return { x: Math.round(Number(posMatch[1])), y: Math.round(Number(posMatch[2])) };
};

const parseLinearGradient = (css: string): IGradientState => {
  const angleMatch: RegExpMatchArray | null = css.match(/linear-gradient\(\s*([\d.]+)deg/);
  const angle: number = angleMatch ? Math.round(Number(angleMatch[1])) : DEFAULT_LINEAR_ANGLE;
  return { type: "linear", angle, stops: parseColorStops(css) };
};

const parseRadialGradient = (css: string): IGradientState => {
  const shapeMatch: RegExpMatchArray | null = css.match(/radial-gradient\(\s*(circle|ellipse)/);
  const shape: RadialShape = shapeMatch ? (shapeMatch[1] as RadialShape) : DEFAULT_RADIAL_SHAPE;
  const sizeMatch: RegExpMatchArray | null = css.match(/(closest-side|closest-corner|farthest-side|farthest-corner)/);
  const size: RadialSize = sizeMatch ? (sizeMatch[1] as RadialSize) : DEFAULT_RADIAL_SIZE;
  return {
    type: "radial",
    angle: DEFAULT_LINEAR_ANGLE,
    stops: parseColorStops(css),
    position: parsePosition(css),
    shape,
    size
  };
};

const parseConicGradient = (css: string): IGradientState => {
  const angleMatch: RegExpMatchArray | null = css.match(/from\s+([\d.]+)deg/);
  const angle: number = angleMatch ? Math.round(Number(angleMatch[1])) : DEFAULT_CONIC_ANGLE;
  return {
    type: "conic",
    angle,
    stops: parseColorStops(css),
    position: parsePosition(css)
  };
};

/**
 * Parse a CSS gradient string into working-state. Returns null when the value
 * is not a gradient. Type is dispatched on the function prefix.
 */
export const parseGradient = (css: string | null | undefined): IGradientState | null => {
  if (!css) {
    return null;
  }
  const trimmed: string = css.trim();
  if (trimmed.startsWith("radial-gradient")) {
    return parseRadialGradient(trimmed);
  }
  if (trimmed.startsWith("conic-gradient")) {
    return parseConicGradient(trimmed);
  }
  if (trimmed.startsWith("linear-gradient")) {
    return parseLinearGradient(trimmed);
  }
  return null;
};
